import { FC, ReactNode } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

interface StatCardProps {
  title: string | ReactNode;
  value: string | number;
  valueColor?: string;
  subtitle?: string | ReactNode;
  showProgressBar?: boolean;
  progressValue?: number;
  progressColor?: string;
  /** When set, renders a ghost overlay at this position to show the previous baseline. */
  baselineProgressValue?: number;
  icon?: ReactNode;
  additionalContent?: ReactNode;
  onClick?: () => void;
}

export const StatCard: FC<StatCardProps> = observer(
  ({
    title,
    value,
    valueColor,
    subtitle,
    showProgressBar = false,
    progressValue = 0,
    progressColor,
    baselineProgressValue,
    icon,
    additionalContent,
    onClick,
  }) => {
    const theme = useTheme();

    return (
      <Paper
        elevation={0}
        onClick={onClick}
        sx={{
          flex: 1,
          minWidth: '240px',
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[300]}`,
          p: 3,
          backgroundColor: 'white',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          '&:hover': onClick ? {
            borderColor: theme.palette.grey[400],
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          } : {},
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Title */}
          <Typography variant="textSM_regular" >
            {title}
          </Typography>

          {/* Large Value */}
          <Typography
            variant="displaySM_regular"
            sx={{
              color: valueColor || 'text.primary',
              mt: 1.75,
            }}
          >
            {value}
          </Typography>

          {/* Progress Bar (optional) */}
          {showProgressBar && (
            <Box
              sx={{
                position: 'relative',
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.grey[200],
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${Math.min(100, Math.max(0, progressValue))}%`,
                  backgroundColor: progressColor || theme.palette.primary.main,
                  borderRadius: 4,
                  transition: 'width 0.3s ease',
                }}
              />
              {baselineProgressValue != null && (
                <>
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: `${Math.min(100, Math.max(0, baselineProgressValue))}%`,
                      backgroundColor: progressColor || theme.palette.primary.main,
                      opacity: 0.6,
                      backgroundImage:
                        'repeating-linear-gradient(45deg, transparent 0 3px, rgba(255,255,255,0.55) 3px 6px)',
                      borderRadius: 4,
                    }}
                  />
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      top: -1,
                      bottom: -1,
                      left: `${Math.min(100, Math.max(0, baselineProgressValue))}%`,
                      width: 3,
                      backgroundColor: progressColor || theme.palette.primary.main,
                      opacity: 1,
                      transform: 'translateX(-1.5px)',
                      boxShadow: '0 0 4px 1px rgba(0,0,0,0.18)',
                    }}
                  />
                </>
              )}
            </Box>
          )}

          {/* Subtitle or Additional Info */}
          {subtitle && (
            <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
              {subtitle}
            </Typography>
          )}

          {/* Icon with text (optional) */}
          {icon && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {icon}
            </Box>
          )}

          {/* Additional Custom Content */}
          {additionalContent}
        </Box>
      </Paper>
    );
  },
);
